/**
 * Shared client contract for disconnecting a connected app.
 *
 * Every provider's `DELETE /<provider>-link/:oauthId` route in dream-be goes
 * through the same handler: it revokes access at the provider where the API
 * allows it, always deletes the stored credentials, and — when
 * `deleteLearnedData=true` — removes the memories learned solely from that
 * account. `GET /connected-apps/:oauthId/deletion-preview` reports what that
 * second step would touch so the user can decide.
 */
import type { LearnedDataPreview } from '@dreamai/types';

export type {
  ConnectedAppDeletionPreviewResponse as ConnectedAppDeletionPreview,
  ConnectedAppRevokeStatus as RevokeStatus,
  DisconnectConnectedAppResponse,
  LearnedDataCounts,
  LearnedDataPreview,
} from '@dreamai/types';

/** What the user is about to disconnect; drives the confirmation modal. */
export interface DisconnectTarget {
  oauthId: number;
  /** Account identifier shown in the modal (email, workspace name…). */
  label: string;
  /** Provider name shown in the modal ("Google", "Notion"…). */
  providerLabel: string;
  /** Provider-specific consequences to append to the standard warning. */
  extraWarning?: string;
  run: (deleteLearnedData: boolean) => Promise<void>;
}

export function deletionPreviewPath(oauthId: number): string {
  return `/connected-apps/${oauthId}/deletion-preview`;
}

export function disconnectPath(
  providerBase: string,
  oauthId: number,
  deleteLearnedData: boolean,
): string {
  const path = `${providerBase}/${oauthId}`;
  return deleteLearnedData ? `${path}?deleteLearnedData=true` : path;
}

export function totalDeletable(preview: LearnedDataPreview): number {
  return (
    preview.facts.deletable +
    preview.relationships.deletable +
    preview.entityNotes.deletable
  );
}

export function totalRetained(preview: LearnedDataPreview): number {
  return [preview.facts, preview.relationships, preview.entityNotes].reduce(
    (sum, counts) =>
      sum + counts.retainedMixedSource + counts.retainedUserStated,
    0,
  );
}
